import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { Avatar, Username } from "../components/UserBadge";
import LastPostMember from "../components/LastPostMember";
import Sidebar from "../components/Sidebar";
import ChatboxWidget from "../components/ChatboxWidget";
import { useAuth } from "../context/AuthContext";
import { Pin, Lock, Plus, MessageSquare, Eye, ChevronRight, Folder, FileText } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useT } from "../i18n";

export default function Board() {
  const { boardId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const queryClient = useQueryClient();
  const t = useT();

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60000,
  });

  const { data: boardsData } = useQuery({
    queryKey: ["boards"],
    queryFn: async () => (await api.get("/boards")).data,
    staleTime: 60000,
  });

  const allBoards = boardsData || [];
  const subForums = allBoards.filter(b => b.parent_id === boardId);

  // For breadcrumbs, find parent forum and category
  let parentBoard = null;
  let categoryLabel = "";
  if (data?.board) {
    if (data.board.parent_id) {
      parentBoard = allBoards.find(b => b.id === data.board.parent_id);
    }
    const categoriesList = settings?.categories || [];
    const cat = categoriesList.find(c => c.id === data.board.game);
    categoryLabel = cat ? cat.label : data.board.game?.toUpperCase();
  }

  useEffect(() => {
    if (!settings) return;
    const pageSize = settings.forums_topics_per_page || 25;
    api.get(`/boards/${boardId}?page_size=${pageSize}`).then((r) => setData(r.data));
  }, [boardId, settings]);

  if (!data) {
    return (
      <div className="flex items-center justify-center p-12 text-zinc-500 font-mono text-xs">
        {t("common.loading") || "Se încarcă..."}
      </div>
    );
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    const { data: res } = await api.post("/topics", { board_id: data.board.id, title, content });
    queryClient.invalidateQueries({ queryKey: ["boards"] });
    queryClient.invalidateQueries({ queryKey: ["board", data.board.id] });
    navigate(`/topic/${res.topic.id}`);
  };

  const boardColor = data.board?.color || allBoards.find(b => b.id === boardId)?.color || "var(--tf-primary)";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-3 space-y-6">
        
        {/* Breadcrumb Navigation */}
        <div className="flex items-center text-xs font-mono text-zinc-400 flex-wrap gap-1.5 bg-[#06080d] border border-[#141824] px-4 py-2.5 shadow-sm" data-testid="breadcrumb">
          <Link to="/forums" className="hover:text-[var(--tf-primary)] transition-colors">
            {t("nav.forums") || "FORUM"}
          </Link>
          <ChevronRight className="w-3 h-3 text-zinc-600" />
          {categoryLabel && (
            <>
              <span className="text-zinc-300 font-chakra uppercase font-bold">{categoryLabel}</span>
              <ChevronRight className="w-3 h-3 text-zinc-600" />
            </>
          )}
          {parentBoard && (
            <>
              <Link to={`/forums/${parentBoard.id}`} className="hover:text-[var(--tf-primary)] transition-colors">
                {parentBoard.name}
              </Link>
              <ChevronRight className="w-3 h-3 text-zinc-600" />
            </>
          )}
          <span className="text-white font-chakra font-bold tracking-wide">{data.board.name}</span>
        </div>

        {/* Chatbox Widget */}
        <ChatboxWidget currentBoardId={boardId} />

        {/* SUB-FORUMS / SUB-CATEGORII */}
        {subForums.length > 0 && (
          <section className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl" data-testid="subforums-section">
            <div className="px-4 sm:px-5 py-3 bg-[#06080d] border-b border-[#141824] flex items-center justify-between">
              <h3 className="font-chakra font-black text-xs uppercase tracking-wider text-[var(--tf-primary)] flex items-center gap-2">
                <Folder className="w-3.5 h-3.5" />
                <span>{t("board.subforums", "SUB-CATEGORII")}</span>
              </h3>
              <span className="text-[10px] font-mono text-zinc-500">
                {subForums.length} SUB-FORUMURI
              </span>
            </div>

            <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-2 gap-3.5 bg-[#06080d]/50">
              {subForums.map((s) => (
                <Link 
                  key={s.id} 
                  to={`/forums/${s.id}`} 
                  className="block bg-[#06080d] hover:bg-[#0c1018] p-3.5 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-all duration-200 group relative overflow-hidden shadow-sm hover:translate-x-1"
                >
                  <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-[var(--tf-primary)] opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 flex items-center justify-center bg-black/60 border border-[#181e2b] text-zinc-500 group-hover:text-[var(--tf-primary)] group-hover:border-[var(--tf-primary)]/40 transition-colors shrink-0">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-chakra font-black text-sm text-white group-hover:text-[var(--tf-primary)] uppercase tracking-wide truncate transition-colors">
                      {s.name}
                    </span>
                  </div>

                  {s.description && (
                    <div className="text-xs font-mono text-zinc-400 mt-2 pl-8 line-clamp-1">
                      {s.description}
                    </div>
                  )}

                  <div className="text-[10px] font-mono text-zinc-500 mt-2.5 pl-8 flex items-center gap-3">
                    <span className="text-amber-400 font-bold bg-amber-500/10 border border-amber-500/20 px-2 py-0.5">
                      {s.topic_count || 0} {t("board.topics", "Topicuri")}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Board Main Header Banner */}
        <header className="bg-[#090c12] border border-[#151924] p-5 sm:p-6 relative overflow-hidden shadow-2xl">
          {/* Top ambient highlight */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary)] to-transparent opacity-80" />

          <div className="flex items-end justify-between gap-4 flex-wrap relative z-10">
            <div className="space-y-1">
              <span className="inline-block px-2.5 py-0.5 text-[10px] font-chakra font-black tracking-widest uppercase bg-[var(--tf-primary)]/10 text-[var(--tf-primary)] border border-[var(--tf-primary)]/30">
                {data.board.game ? data.board.game.toUpperCase() : "FORUM"}
              </span>
              <h1 className="font-chakra font-black text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wider text-white" data-testid="board-title">
                {data.board.name}
              </h1>
              {data.board.description && (
                <p className="text-xs sm:text-sm text-zinc-400 font-mono">
                  {data.board.description}
                </p>
              )}
              <div className="flex items-center gap-3 pt-2 text-xs font-mono text-zinc-400">
                <span>
                  <span className="text-zinc-500">{t("board.topics", "Topicuri")}:</span>{" "}
                  <strong className="text-white">{data.total || 0}</strong>
                </span>
              </div>
            </div>

            {user && (
              <button 
                onClick={() => setCreating((v) => !v)} 
                className="bg-[var(--tf-primary)] hover:brightness-110 text-black font-chakra font-black text-xs uppercase px-4 py-2.5 flex items-center gap-1.5 transition-all shadow-[0_0_15px_rgba(255,79,0,0.3)] cursor-pointer" 
                data-testid="board-new-topic"
              >
                <Plus className="w-4 h-4 stroke-[3]" /> 
                <span>{t("board.new_topic", "TOPIC NOU")}</span>
              </button>
            )}
          </div>
        </header>

        {/* New Topic Creation Form */}
        {creating && (
          <form onSubmit={handleCreate} className="bg-[#090c12] border border-[#151924] p-5 sm:p-6 space-y-4 shadow-2xl" data-testid="new-topic-form">
            <h3 className="font-chakra font-black text-sm uppercase tracking-wider text-white flex items-center gap-2 border-b border-[#141824] pb-3">
              <FileText className="w-4 h-4 text-[var(--tf-primary)]" />
              <span>Creează un Topic Nou</span>
            </h3>
            <input 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              placeholder={t("board.topic_title_placeholder", "Titlu topic...")} 
              className="w-full bg-[#06080d] border border-[#141824] focus:border-[var(--tf-primary)] text-white p-3 text-sm font-sans outline-none transition-colors" 
              required 
              minLength={3} 
              data-testid="new-topic-title" 
            />
            <textarea 
              value={content} 
              onChange={(e) => setContent(e.target.value)} 
              placeholder={t("board.topic_content_placeholder", "Ce dorești să transmiți comunității?")} 
              rows={5} 
              className="w-full bg-[#06080d] border border-[#141824] focus:border-[var(--tf-primary)] text-white p-3 text-sm font-sans outline-none transition-colors" 
              required 
              data-testid="new-topic-content" 
            />
            <div className="flex items-center gap-3 pt-2">
              <button 
                type="submit" 
                className="bg-[var(--tf-primary)] hover:brightness-110 text-black font-chakra font-black text-xs uppercase px-5 py-2.5 transition-all cursor-pointer" 
                data-testid="new-topic-submit"
              >
                {t("common.create", "Publică")}
              </button>
              <button 
                type="button" 
                onClick={() => setCreating(false)} 
                className="bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] text-zinc-400 hover:text-white font-chakra font-bold text-xs uppercase px-4 py-2.5 transition-all cursor-pointer"
              >
                {t("common.cancel", "Renunță")}
              </button>
            </div>
          </form>
        )}

        {/* Topics List Table Header */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] font-chakra font-black uppercase text-zinc-400 tracking-widest bg-[#06080d] border border-[#141824] shadow-sm">
          <div className="col-span-6">{t("board.col_topic", "TOPIC")}</div>
          <div className="col-span-2 text-center">{t("board.col_stats", "STATISTICI")}</div>
          <div className="col-span-4">{t("board.col_lastpost", "ULTIMUL RĂSPUNS")}</div>
        </div>

        {/* Topics List Rows */}
        <div className="bg-[#090c12] border border-[#151924] divide-y divide-[#141824] shadow-2xl overflow-hidden">
          {data.topics.length === 0 && (
            <div className="p-8 text-center text-zinc-500 font-mono text-xs bg-[#06080d]">
              {t("board.no_topics", "Niciun topic în această categorie. Fii primul care deschide o discuție!")}
            </div>
          )}
          {data.topics.map((topic) => (
            <div 
              key={topic.id} 
              className="grid grid-cols-12 gap-3 sm:gap-4 px-4 sm:px-5 py-4 bg-[#06080d] hover:bg-[#0c1018] transition-all duration-200 group items-center border-l-2 border-l-transparent hover:border-l-[var(--tf-primary)]" 
              data-testid={`topic-row-${topic.id}`}
            >
              <div className="col-span-12 md:col-span-6 flex items-start gap-3">
                <Avatar user={topic.author || { username: topic.author_username, loadout: {} }} size={38} hover />
                <div className="flex-1 min-w-0">
                  <Link to={`/topic/${topic.id}`} className="block">
                    <div className="flex items-center gap-2 flex-wrap">
                      {topic.pinned && <Pin className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                      {topic.locked && <Lock className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
                      <span className="font-chakra font-bold text-sm text-white group-hover:text-[var(--tf-primary)] uppercase tracking-wide line-clamp-1 transition-colors">
                        {topic.title}
                      </span>
                    </div>
                  </Link>
                  <div className="text-xs font-mono text-zinc-400 mt-1 flex items-center gap-2 flex-wrap">
                    <Username user={topic.author || { username: topic.author_username, loadout: {} }} className="text-xs font-chakra" asText />
                    <span>·</span>
                    <span>{topic.created_at ? formatDistanceToNow(new Date(topic.created_at)) + ` ${t("time.ago") || "în urmă"}` : ""}</span>
                  </div>
                </div>
              </div>

              {/* Stats Replies & Views */}
              <div className="col-span-12 md:col-span-2 flex md:flex-col items-center md:justify-center text-xs font-mono gap-4 md:gap-1 mt-2 md:mt-0 pt-2 md:pt-0 border-t border-[#141824] md:border-none">
                <span className="text-white font-bold flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3 text-zinc-500" />
                  <span>{topic.post_count || 0} răspunsuri</span>
                </span>
                <span className="text-zinc-500 flex items-center gap-1.5">
                  <Eye className="w-3 h-3 text-zinc-600" />
                  <span>{topic.views || 0} vizualizări</span>
                </span>
              </div>

              {/* Last Post Member Column */}
              <div className="col-span-12 md:col-span-4 items-center text-xs mt-2 md:mt-0 pt-2 md:pt-0 border-t border-[#141824] md:border-none">
                <LastPostMember
                  user={topic.last_post_user || (topic.last_post_username ? { username: topic.last_post_username } : topic.author)}
                  time={topic.last_post_at || topic.created_at}
                  customColor={boardColor}
                  topicLink={`/topic/${topic.id}`}
                />
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Sidebar />
      </div>
    </div>
  );
}
